const faqs = {
  en: {
    pageTitle: "Frequently Asked Questions",
    headline: "Get ready for your next trip to Asia-Pacific!",
    subheadline:
      "Explore our Frequently Asked Questions when setting up your mobile payment apps accepted in the region.",
    tabsAriaLabel: "FAQ payment providers",
    tabs: {
      general: "General",
      alipay: "AliPay",
      weixin: "Weixin (WeChat)*",
      grab: "Grab",
    },
    weixinTabDisclosure:
      "The official name of the mobile payment app is Weixin Pay. WeChat is the international version of Weixin for users traveling to China. Within China, the app is known as Weixin.",
    grabTabDisclaimer:
      "** Users must top up their Grab app prior to making an in-person payment when traveling in Singapore, the Philippines, or Malaysia. However, Grab's top-up functionality is NOT available for foreign travelers in Thailand when paying in-person with a foreign card as Thai ID is required. Discover® Network Cardholders visiting Thailand can download and add their card to the Grab app for rideshare, food delivery, and other in-app services that do not require top ups prior to payment.",
    footnoteLabel: "Footnote",
    questions: {
      general: [
        {
          question: "Why do I need to use these mobile apps for payment?",
          content:
            "Alipay, Weixin Pay, and Grab are accepted by most merchants and service providers, from major retailers and restaurants to small street vendors, across the region. By using these apps, you'll be able to pay via merchant-preferred methods and you won't need to carry large amounts of cash during your trip.",
        },
        {
          question: "Which mobile app should I use when I travel to China?",
          content:
            "We encourage Cardholders traveling to China to add their cards to Alipay and Weixin Pay.",
        },
        {
          question:
            "Which mobile app should I use when I travel to Singapore, Malaysia, Thailand or the Philippines?",
          content:
            "We encourage Cardholders traveling to Singapore, Thailand, Malaysia, or the Philippines to add their cards to Grab.",
        },
        {
          question: "How do I set up these mobile apps and add my card?",
          content: [
            {
              type: "tabLinks",
              before: "Go to the Frequently Asked Questions for ",
              after:
                " for detailed information on how to install and add your card to each mobile app.",
              separator: ", ",
              conjunction: " and ",
              items: [
                { tab: "alipay", label: "Alipay" },
                { tab: "weixin", label: "Weixin Pay" },
                { tab: "grab", label: "Grab" },
              ],
            },
          ],
        },
        {
          question:
            "When do I need to complete this process in relation to my travel plans?",
          content:
            "We recommend installing these mobile apps and adding your card before you travel. Attempting this upon arrival without access to a local phone number or reliable internet may be challenging.",
        },
        {
          question: "Do these mobile apps cost money?",
          content:
            "No, Alipay, Weixin Pay, and Grab are all free to download and register. Depending on the payment amount, the app operating company may charge a transaction fee.",
        },
      ],
      alipay: [
        {
          question: "What is Alipay and how does it work?",
          content:
            "Alipay is a leading open platform for digital payment and daily services, operated by Ant Group, an affiliate of Alibaba. It enables domestic and international users to make secure digital payments at merchants across mainland China.",
        },
        {
          question:
            "What are the benefits of using Alipay as international traveler in China?",
          content:
            "Alipay is widely accepted by merchants across China, offering a convenient and secure payment method for international travelers. In addition to payments, Alipay offers easy access to ride hailing, public transport, hotel reservations, and tickets for tourist attractions, streamlining the travel experience. Note that some of these services are provided by third-party operators, ensuring a broad range of options tailored to your needs.",
        },
        {
          question: "How do I sign up for and set up an Alipay account?",
          content: [
            "To use Alipay as an international traveler:",
            {
              type: "p",
              text: "Download the App and Register an Account:",
              bold: true,
            },
            {
              type: "ul",
              items: [
                "Available on both Apple App Store and Google Play Store.",
                'Open the app, enter your mobile phone number, and select "Next." You can also register with your email or Apple ID.',
                "Follow the prompts to complete registration.",
              ],
            },
            { type: "p", text: "Add a Card:", bold: true },
            {
              type: "ul",
              items: [
                'On the home screen, select "Add Now", then add an international card like your Discover® Network Card (Discover® / Diners Club® / Network Alliance Card).',
              ],
            },
            {
              type: "links",
              items: [
                {
                  label: "Learn more",
                  srLabel: "how do I set up an Alipay account",
                  href: "https://www.alipayplus.com/pay-in-the-chinese-mainland/",
                },
              ],
            },
          ],
        },
        {
          question: "Can I use Alipay without a Chinese bank account?",
          content:
            "Yes, international visitors can connect their Alipay accounts with international cards like your Discover® Network Card (Discover® / Diners Club® / Network Alliance Card).",
        },
        {
          question: "Can I use Alipay without a local phone number?",
          content:
            "Yes, international numbers are supported. Cardholders can register an account with a phone number and email address or Apple ID.",
        },
        {
          question:
            "How do I use Alipay to pay in-person at a store or restaurant?",
          content: [
            "To pay in-store using Alipay, there are two options:",
            {
              type: "ul",
              items: [
                {
                  label: "Merchant-presented QR Code:",
                  text: ' Look for a QR code displayed by the merchant, then open your Alipay app and use the "Scan" function to capture the code. This will initiate the payment.',
                  image: "alipay-howto1",
                },
                {
                  label: "Cardholder-presented QR Code:",
                  text: ' Open your Alipay app and select the "Pay / Receive" option on the top header of the app, which will generate a unique QR code. Have the merchant scan this QR code to complete the transaction.',
                  image: "alipay-howto2",
                },
              ],
            },
          ],
        },
        {
          question:
            "Are there any fees or exchange rates associated with using Alipay?",
          content: [
            "Transactions over a certain amount might incur additional fees. Currently, the fee for transactions less than RMB 200 is waived. Please refer to Alipay's policies for the most up-to-date information. Exchange rates are subject to the issuer's rates.",
            {
              type: "p",
              label: "Limit of transaction",
              text: " - Please refer to Alipay's policies for the most up-to-date information. Exchange rates are subject to the issuer's rates.",
            },
            "Currently, there are transaction limits of:",
            {
              type: "ul",
              items: [
                "Single transaction limit: US$5,000.",
                "Annual transaction limit: US$50,000.",
              ],
            },
            "For the most up-to-date information and policies, and local assistance, please contact Alipay at:",
            {
              type: "ul",
              items: [
                "0571-26886000 if calling from a local Chinese number or",
                "+86 571 2688 6000 if calling from an overseas number",
              ],
            },
            "Service Hours: Monday – Sunday, 08:00-24:00, GMT+8",
          ],
        },
      ],
      weixin: [
        {
          question: "What is Weixin Pay (WeChat)?",
          content:
            "Weixin Pay is China's leading mobile payment and digital wallet service built into WeChat, one of the most popular social media and messaging apps in China. Note that the official name of the mobile payment app is Weixin Pay. WeChat is the international version of Weixin for users traveling to China. Within China, the app is known as Weixin.",
        },
        {
          question:
            "What are the benefits of using Weixin Pay (WeChat) as international traveler in China?",
          content:
            "Weixin Pay is widely accepted at merchants across China, providing a convenient and secure way to make payments. It also eliminates the need to carry large amounts of cash.",
        },
        {
          question:
            "How do I sign up for and set up a Weixin Pay (WeChat) account?",
          content: [
            "To use Weixin Pay as an international traveler:",
            { type: "p", text: "Download the WeChat App:", bold: true },
            {
              type: "ul",
              items: [
                "Available on both Apple App Store and Google Play Store.",
              ],
            },
            { type: "p", text: "Register an Account:", bold: true },
            {
              type: "p",
              text: 'Requires a valid phone number and your Discover® Network Card (Discover® / Diners Club® / Network Alliance Card). Open the app and select "Sign Up."',
            },
            {
              type: "ul",
              items: [
                "Enter your phone number and receive a verification code.",
                "Input the verification code and complete your profile.",
              ],
            },
            { type: "p", text: "Add a Card:", bold: true },
            {
              type: "ul",
              items: [
                'Select "Me" in the bottom navigation bar.',
                'Select "Services", followed by "Wallet", then "Bank Cards".',
                "Fill in the required information to add your card.",
              ],
            },
            {
              type: "links",
              items: [
                {
                  label: "Learn more",
                  srLabel: "how do I set up a Weixin Pay account",
                  href: "https://www.wechat.com/en",
                },
              ],
            },
          ],
        },
        {
          question: "Can I use Weixin Pay without a Chinese bank account?",
          content:
            "Yes, foreign visitors can connect their Weixin Pay accounts with international cards like your Discover® Network Card (Discover® / Diners Club® / Network Alliance Card).",
        },
        {
          question: "Can I use Weixin Pay without a local phone number?",
          content:
            "Yes, international numbers are supported. Cardholders can register an account with both a phone number and an email address.",
        },
        {
          question:
            "How do I use Weixin Pay to pay in-person at a store or restaurant?",
          content: [
            "To pay in-store using Weixin Pay, there are two options:",
            {
              type: "ul",
              items: [
                {
                  label: "Merchant-presented QR Code Payment:",
                  text: ' Look for a QR code displayed by the merchant, then open Weixin Pay (WeChat app) and use the "Scan" function to capture the code. Follow the prompts to complete the payment.',
                  image: "wechat-howto1",
                },
                {
                  label:
                    "Cardholder-presented QR Code (Also known as Quick Pay):",
                  text: ' Open Weixin Pay (WeChat app) and press "Money" under the "+" mark at top right corner to display your "Quick Pay" option, which will generate a unique QR code. Have the merchant scan this QR code to complete the transaction.',
                  image: "wechat-howto2",
                },
              ],
            },
          ],
        },
        {
          question:
            "Are there any fees or exchange rates associated with using Weixin Pay?",
          content: [
            "A transaction fee of 3% will be applied to international card transactions exceeding RMB 200 per transaction. In the event of a refund, the service fee will be refunded proportionally based on the transaction amount. Please refer to Weixin Pay's policies for the most up-to-date information. Exchange rates are subject to the issuer's rates.",
            {
              type: "p",
              label: "Limit of transaction: ",
              text: "For simplified registration with international cards, the annual limit is RMB 15,000. After supplementing with real-name information, the limits increase to a single transaction limit of RMB 35,000 and an annual limit of RMB 350,000. Please refer to Weixin Pay's policies for the most up-to-date information. Exchange rates are subject to the issuer's rates.",
            },
            {
              type: "links",
              intro:
                "For the most up-to-date information and policies, please refer to Weixin Pay's website:",
              items: [
                {
                  label: "Tencent — Weixin Pay information",
                  href: "https://www.tencent.com/en-us/articles/2201831.html",
                },
              ],
            },
          ],
        },
      ],
      grab: [
        {
          question: "What is Grab / GrabPay?",
          content: [
            "Grab is a mobile app that allows booking and payment for rides, food deliveries, and in-store purchases**. Its digital payment solution is named GrabPay. GrabPay services are becoming increasingly popular in Singapore and the broader South Asian countries, including Malaysia, Thailand, and the Philippines.",
            "The Grab app is also now available in Chinese, Korean, and Japanese, to deliver a more intuitive experience for travelers from China, South Korea, and Japan.",
          ],
        },
        {
          question:
            "What are the benefits of using Grab as an international traveler in Singapore, Malaysia, Thailand, and the Philippines?",
          content:
            "From ride-hailing and food delivery to payments, the Grab app provides international travelers in Singapore, Malaysia, Thailand, and the Philippines with convenience, local insights, and cashless transactions, making navigation and local daily activities efficient.",
        },
        {
          question: "How do I sign up for and set up a Grab account?",
          content: [
            "To use Grab as an international traveler:",
            { type: "p", text: "Download the Grab App:", bold: true },
            {
              type: "ul",
              items: [
                "Available on both Apple App Store and Google Play Store.",
              ],
            },
            { type: "p", text: "Register an Account:", bold: true },
            {
              type: "ul",
              items: [
                "Sign up with your phone number.",
                "Follow the prompts to complete your profile.",
              ],
            },
            { type: "p", text: "Add a Card:", bold: true },
            {
              type: "ul",
              items: [
                'Select "Payment" in the bottom navigation bar.',
                'Select "Payment Methods" then "Credit / Debit Card."',
                "Fill in the required information to add your card.",
              ],
            },
            {
              type: "links",
              items: [
                {
                  label: "Learn more",
                  srLabel: "about setting up a GrabPay account",
                  href: "https://www.grab.com/ph/pay/guide/how-to-pay/",
                },
              ],
            },
          ],
        },
        {
          question:
            "How do I use Grab to pay in-person** at a store or restaurant?",
          content: [
            "To pay in-store using Grab, there are two options:",
            {
              type: "ul",
              items: [
                {
                  label: "Via QR Sticker:",
                  text: ' Tap on "Scan" icon in the top left corner of your Grab app, then scan the merchant\'s QR code and key-in the amount to be paid to the merchant. Swipe to complete the payment.',
                },
                {
                  label: "Via Point-of-Sale Terminal:",
                  text: ' Tap on "Scan" icon in the top left corner of Grab app, then scan the QR code displayed on the merchant\'s terminal. The transaction amount will appear on your screen. Swipe to complete the payment.',
                },
              ],
            },
          ],
        },
        {
          question: "How do I top up my Grab app for in-store purchases**?",
          content: [
            "To top up your Grab app, here are two options:",
            { type: "p", text: "Via an International Card:", bold: true },
            {
              type: "ul",
              items: [
                'Tap on "Payment" in the navigation bar.',
                'Tap on "Cards and Wallets."',
                'Select a top-up value, select your preferred payment method, and tap "Confirm."',
              ],
            },
            { type: "p", text: "Via Online Banking:", bold: true },
            {
              type: "ul",
              items: [
                'Tap on "Payment" in the navigation bar.',
                'Tap on "Online Banking".',
                'Select your top-up value and your preferred payment method, then tap "Confirm."',
                "You will be led to your bank's online banking page. Enter your username and password to login.",
                'Check your transaction details, and if correct, tap "Continue with Transaction."',
              ],
            },
          ],
        },
        {
          question:
            "Are there any fees or exchange rates associated with using Grab?",
          content:
            "Transactions over a certain amount might incur additional fees. The transaction fees are communicated via in-app notification. Please refer to Grab's policies in-app for the most up-to-date information. Exchange rates are subject to the issuer's rates.",
        },
        {
          question:
            "Can I access Grab services through other apps that I'm familiar with?",
          content: [
            "For travelers who may prefer not to download another app, Grab has also teamed up with some of the world's most popular apps to provide travelers visiting Southeast Asia with options to access Grab's ride-hailing services.",
            "Apps such as WeChat, Alipay, Kakao T, and Booking.com users can access Grab's ride-hailing services across Southeast Asia through these apps. Uber users that open the app while in Southeast Asia will also see a message directing them to Grab as Uber's recommended way to travel in the region.",
            {
              type: "links",
              intro:
                "For the most up-to-date information and policies, please refer to Grab's website:",
              items: [
                { label: "Grab – Singapore", href: "https://www.grab.com/sg/" },
                { label: "Grab – Malaysia", href: "https://www.grab.com/my/" },
                {
                  label: "Grab – Philippines",
                  href: "https://www.grab.com/ph/",
                },
                {
                  label: "Grab – Thailand",
                  href: "https://www.grab.com/th/en/",
                },
              ],
            },
          ],
        },
      ],
    },
  },
  es: {
    pageTitle: "Preguntas frecuentes",
    headline: "Prepárate para tu próximo viaje a Asia-Pacífico!",
    subheadline:
      "Explora nuestras Preguntas Frecuentes cuando configuras tus aplicaciones de pago móvil aceptadas en la región.",
    tabsAriaLabel: "Proveedores de pago en las preguntas frecuentes",
    tabs: {
      general: "General",
      alipay: "AliPay",
      weixin: "Weixin (WeChat)*",
      grab: "Grab",
    },
    weixinTabDisclosure:
      "El nombre oficial de la aplicación de pago móvil es Weixin Pay. WeChat es la versión internacional de Weixin para usuarios que viajan a China. Dentro de China, la aplicación se conoce como Weixin.",
    grabTabDisclaimer:
      "**Los usuarios deben recargar su aplicación Grab antes de realizar un pago en persona cuando viajan a Singapur, Filipinas o Malasia. Sin embargo, la función de recarga de Grab NO está disponible para viajeros extranjeros en Tailandia al pagar en persona con una tarjeta extranjera, ya que se requiere identificación tailandesa. Los titulares de tarjeta Discover® Network que visiten Tailandia pueden descargar y añadir su tarjeta a la aplicación Grab para transporte por aplicación, entrega de comida y otros servicios dentro de la aplicación que no requieren recarga previa al pago.",
    footnoteLabel: "Nota al pie",
    questions: {
      general: [
        {
          question:
            "¿Por qué necesito usar estas aplicaciones móviles para pagar?",
          content:
            "Alipay, Weixin Pay y Grab son aceptados por la mayoría de comercios y proveedores de servicios, desde grandes tiendas y restaurantes hasta pequeños vendedores ambulantes, en toda la región. Al usar estas aplicaciones, podrás pagar con los métodos preferidos por los comercios y no necesitarás llevar grandes cantidades de efectivo durante tu viaje.",
        },
        {
          question: "¿Qué aplicación móvil debo usar cuando viaje a China?",
          content:
            "Recomendamos que los titulares de tarjeta que viajen a China añadan sus tarjetas a Alipay y Weixin Pay.",
        },
        {
          question:
            "¿Qué aplicación móvil debo usar cuando viaje a Singapur, Malasia, Tailandia o Filipinas?",
          content:
            "Recomendamos que los titulares de tarjeta que viajen a Singapur, Tailandia, Malasia o Filipinas añadan sus tarjetas a Grab.",
        },
        {
          question:
            "¿Cómo configuro estas aplicaciones móviles y añado mi tarjeta?",
          content: [
            {
              type: "tabLinks",
              before: "Consulta las Preguntas Frecuentes de ",
              after:
                " para obtener información detallada sobre cómo instalar y añadir tu tarjeta a cada aplicación móvil.",
              separator: ", ",
              conjunction: " y ",
              items: [
                { tab: "alipay", label: "Alipay" },
                { tab: "weixin", label: "Weixin Pay" },
                { tab: "grab", label: "Grab" },
              ],
            },
          ],
        },
        {
          question:
            "¿Cuándo debo completar este proceso en relación con mis planes de viaje?",
          content:
            "Recomendamos instalar estas aplicaciones móviles y añadir tu tarjeta antes de viajar. Intentar hacerlo al llegar sin acceso a un número de teléfono local o a internet confiable puede ser difícil.",
        },
        {
          question: "¿Estas aplicaciones móviles tienen costo?",
          content:
            "No, Alipay, Weixin Pay y Grab son gratuitas para descargar y registrarse. Según el monto del pago, la empresa operadora de la aplicación puede cobrar una comisión por transacción.",
        },
      ],
      alipay: [
        {
          question: "¿Qué es Alipay y cómo funciona?",
          content:
            "Alipay es una plataforma abierta líder en pagos digitales y servicios cotidianos, operada por Ant Group, una filial de Alibaba. Permite a usuarios nacionales e internacionales realizar pagos digitales seguros en comercios de toda China continental.",
        },
        {
          question:
            "¿Cuáles son los beneficios de usar Alipay como viajero internacional en China?",
          content:
            "Alipay es ampliamente aceptado por comercios en toda China, ofreciendo un método de pago conveniente y seguro para viajeros internacionales. Además de los pagos, Alipay ofrece fácil acceso a transporte por aplicación, transporte público, reservas de hotel y entradas para atracciones turísticas, simplificando la experiencia de viaje. Ten en cuenta que algunos de estos servicios son proporcionados por operadores externos, lo que garantiza una amplia gama de opciones adaptadas a tus necesidades.",
        },
        {
          question: "¿Cómo me registro y configuro una cuenta de Alipay?",
          content: [
            "Para usar Alipay como viajero internacional:",
            {
              type: "p",
              text: "Descarga la aplicación y registra una cuenta:",
              bold: true,
            },
            {
              type: "ul",
              items: [
                "Disponible en Apple App Store y Google Play Store.",
                'Abre la aplicación, ingresa tu número de teléfono móvil y selecciona "Siguiente". También puedes registrarte con tu correo electrónico o Apple ID.',
                "Sigue las indicaciones para completar el registro.",
              ],
            },
            { type: "p", text: "Añade una tarjeta:", bold: true },
            {
              type: "ul",
              items: [
                'En la pantalla de inicio, selecciona "Añadir ahora" y luego añade una tarjeta internacional como tu tarjeta Discover® Network (Discover® / Diners Club® / Network Alliance Card).',
              ],
            },
            {
              type: "links",
              items: [
                {
                  label: "Más información",
                  srLabel: "cómo configurar una cuenta de Alipay",
                  href: "https://www.alipayplus.com/pay-in-the-chinese-mainland/",
                },
              ],
            },
          ],
        },
        {
          question: "¿Puedo usar Alipay sin una cuenta bancaria china?",
          content:
            "Sí, los visitantes internacionales pueden vincular sus cuentas de Alipay con tarjetas internacionales como tu tarjeta Discover® Network (Discover® / Diners Club® / Network Alliance Card).",
        },
        {
          question: "¿Puedo usar Alipay sin un número de teléfono local?",
          content:
            "Sí, se admiten números internacionales. Los titulares de tarjeta pueden registrar una cuenta con un número de teléfono y dirección de correo electrónico o Apple ID.",
        },
        {
          question:
            "¿Cómo uso Alipay para pagar en persona en una tienda o restaurante?",
          content: [
            "Para pagar en tienda con Alipay, hay dos opciones:",
            {
              type: "ul",
              items: [
                {
                  label: "Código QR presentado por el comercio:",
                  text: ' Busca un código QR mostrado por el comercio, abre tu aplicación Alipay y usa la función "Escanear" para capturar el código. Esto iniciará el pago.',
                  image: "alipay-howto1",
                },
                {
                  label: "Código QR presentado por el titular de la tarjeta:",
                  text: ' Abre tu aplicación Alipay y selecciona la opción "Pagar / Recibir" en la parte superior de la aplicación, lo que generará un código QR único. Pide al comercio que escanee este código para completar la transacción.',
                  image: "alipay-howto2",
                },
              ],
            },
          ],
        },
        {
          question:
            "¿Hay comisiones o tipos de cambio asociados con el uso de Alipay?",
          content: [
            "Las transacciones por encima de cierto monto pueden generar comisiones adicionales. Actualmente, la comisión para transacciones menores de 200 RMB está exenta. Consulta las políticas de Alipay para obtener la información más actualizada. Los tipos de cambio están sujetos a las tarifas del emisor.",
            "Actualmente, los límites de transacción son:",
            {
              type: "ul",
              items: [
                "Límite por transacción única: US$5,000.",
                "Límite de transacciones anuales: US$50,000.",
              ],
            },
            "Para obtener la información y políticas más actualizadas, y asistencia local, contacta a Alipay en:",
            {
              type: "ul",
              items: [
                "0571-26886000 si llamas desde un número chino local o",
                "+86 571 2688 6000 si llamas desde el extranjero",
              ],
            },
            "Horario de atención: lunes a domingo, 08:00-24:00, GMT+8",
          ],
        },
      ],
      weixin: [
        {
          question: "¿Qué es Weixin Pay (WeChat)?",
          content:
            "Weixin Pay es el principal servicio de pago móvil y billetera digital de China, integrado en WeChat, una de las aplicaciones de redes sociales y mensajería más populares de China. Nota el nombre oficial de la aplicación de pago móvil es Weixin Pay. WeChat es la versión internacional de Weixin para usuarios que viajan a China. Dentro de China, la aplicación se conoce como Weixin.",
        },
        {
          question:
            "¿Cuáles son los beneficios de usar Weixin Pay (WeChat) como viajero internacional en China?",
          content:
            "Weixin Pay es ampliamente aceptado en comercios de toda China, ofreciendo una forma conveniente y segura de realizar pagos. También elimina la necesidad de llevar grandes cantidades de efectivo.",
        },
        {
          question:
            "¿Cómo me registro y configuro una cuenta de Weixin Pay (WeChat)?",
          content: [
            "Para usar Weixin Pay como viajero internacional:",
            { type: "p", text: "Descarga la aplicación WeChat:", bold: true },
            {
              type: "ul",
              items: ["Disponible en Apple App Store y Google Play Store."],
            },
            { type: "p", text: "Registra una cuenta:", bold: true },
            {
              type: "p",
              text: 'Requiere un número de teléfono válido y tu tarjeta Discover® Network (Discover® / Diners Club® / Network Alliance Card). Abre la aplicación y selecciona "Registrarse".',
            },
            {
              type: "ul",
              items: [
                "Ingresa tu número de teléfono y recibe un código de verificación.",
                "Introduce el código de verificación y completa tu perfil.",
              ],
            },
            { type: "p", text: "Añade una tarjeta:", bold: true },
            {
              type: "ul",
              items: [
                'Selecciona "Yo" en la barra de navegación inferior.',
                'Selecciona "Servicios", luego "Billetera" y después "Tarjetas bancarias".',
                "Completa la información requerida para añadir tu tarjeta.",
              ],
            },
            {
              type: "links",
              items: [
                {
                  label: "Más información",
                  srLabel: "cómo configurar una cuenta de Weixin Pay",
                  href: "https://www.wechat.com/en",
                },
              ],
            },
          ],
        },
        {
          question: "¿Puedo usar Weixin Pay sin una cuenta bancaria china?",
          content:
            "Sí, los visitantes extranjeros pueden vincular sus cuentas de Weixin Pay con tarjetas internacionales como tu tarjeta Discover® Network (Discover® / Diners Club® / Network Alliance Card).",
        },
        {
          question: "¿Puedo usar Weixin Pay sin un número de teléfono local?",
          content:
            "Sí, se admiten números internacionales. Los titulares de tarjeta pueden registrar una cuenta con un número de teléfono y una dirección de correo electrónico.",
        },
        {
          question:
            "¿Cómo uso Weixin Pay para pagar en persona en una tienda o restaurante?",
          content: [
            "Para pagar en tienda con Weixin Pay, hay dos opciones:",
            {
              type: "ul",
              items: [
                {
                  label: "Pago con código QR presentado por el comercio:",
                  text: ' Busca un código QR mostrado por el comercio, abre Weixin Pay (aplicación WeChat) y usa la función "Escanear" para capturar el código. Sigue las indicaciones para completar el pago.',
                  image: "wechat-howto1",
                },
                {
                  label:
                    "Código QR presentado por el titular de la tarjeta (también conocido como Pago rápido):",
                  text: ' Abre Weixin Pay (aplicación WeChat) y pulsa "Dinero" bajo el signo "+" en la esquina superior derecha para mostrar tu opción de "Pago rápido", que generará un código QR único. Pide al comercio que escanee este código para completar la transacción.',
                  image: "wechat-howto2",
                },
              ],
            },
          ],
        },
        {
          question:
            "¿Hay comisiones o tipos de cambio asociados con el uso de Weixin Pay?",
          content: [
            "Se aplicará una comisión del 3% a las transacciones con tarjeta internacional que superen los 200 RMB por transacción. En caso de reembolso, la comisión de servicio se reembolsará de forma proporcional según el monto de la transacción. Consulta las políticas de Weixin Pay para obtener la información más actualizada. Los tipos de cambio están sujetos a las tarifas del emisor.",
            "Para el registro simplificado con tarjetas internacionales, el límite anual es de 15,000 RMB. Después de completar la información de identidad real, los límites aumentan a un límite por transacción de 35,000 RMB y un límite anual de 350,000 RMB. Consulta las políticas de Weixin Pay para obtener la información más actualizada. Los tipos de cambio están sujetos a las tarifas del emisor.",
            {
              type: "links",
              intro:
                "Para obtener la información y políticas más actualizadas, consulta el sitio web de Weixin Pay:",
              items: [
                {
                  label: "Tencent — Información de Weixin Pay",
                  href: "https://www.tencent.com/en-us/articles/2201831.html",
                },
              ],
            },
          ],
        },
      ],
      grab: [
        {
          question: "¿Qué es Grab / GrabPay?",
          content: [
            "Grab es una aplicación móvil que permite reservar y pagar viajes, entregas de comida y compras en tienda*. Su solución de pago digital se llama GrabPay. Los servicios de GrabPay son cada vez más populares en Singapur y en los países del sudeste asiático, incluidos Malasia, Tailandia y Filipinas.",
            "La aplicación Grab también está disponible en chino, coreano y japonés, para ofrecer una experiencia más intuitiva a los viajeros de China, Corea del Sur y Japón.",
          ],
        },
        {
          question:
            "¿Cuáles son los beneficios de usar Grab como viajero internacional en Singapur, Malasia, Tailandia y Filipinas?",
          content:
            "Desde transporte por aplicación y entrega de comida hasta pagos, la aplicación Grab ofrece a los viajeros internacionales en Singapur, Malasia, Tailandia y Filipinas comodidad, información local y transacciones sin efectivo, haciendo más eficiente la navegación y las actividades diarias locales.",
        },
        {
          question: "¿Cómo me registro y configuro una cuenta de Grab?",
          content: [
            "Para usar Grab como viajero internacional:",
            { type: "p", text: "Descarga la aplicación Grab:", bold: true },
            {
              type: "ul",
              items: ["Disponible en Apple App Store y Google Play Store."],
            },
            { type: "p", text: "Registra una cuenta:", bold: true },
            {
              type: "ul",
              items: [
                "Regístrate con tu número de teléfono.",
                "Sigue las indicaciones para completar tu perfil.",
              ],
            },
            { type: "p", text: "Añade una tarjeta:", bold: true },
            {
              type: "ul",
              items: [
                'Selecciona "Pago" en la barra de navegación inferior.',
                'Selecciona "Métodos de pago" y luego "Tarjeta de crédito / débito".',
                "Completa la información requerida para añadir tu tarjeta.",
              ],
            },
            {
              type: "links",
              items: [
                {
                  label: "Más información",
                  srLabel: "GrabPay",
                  href: "https://www.grab.com/ph/pay/guide/how-to-pay/",
                },
              ],
            },
          ],
        },
        {
          question:
            "¿Cómo uso Grab para pagar en persona* en una tienda o restaurante?",
          content: [
            "Para pagar en tienda con Grab, hay dos opciones:",
            {
              type: "ul",
              items: [
                {
                  label: "Mediante etiqueta QR:",
                  text: ' Pulsa el icono "Escanear" en la esquina superior izquierda de tu aplicación Grab, escanea el código QR del comercio e ingresa el monto a pagar. Desliza para completar el pago.',
                },
                {
                  label: "Mediante terminal punto de venta:",
                  text: ' Pulsa el icono "Escanear" en la esquina superior izquierda de la aplicación Grab, escanea el código QR mostrado en la terminal del comercio. El monto de la transacción aparecerá en tu pantalla. Desliza para completar el pago.',
                },
              ],
            },
          ],
        },
        {
          question: "¿Cómo recargo mi aplicación Grab para compras en tienda*?",
          content: [
            "Para recargar tu aplicación Grab, hay dos opciones:",
            {
              type: "p",
              text: "Mediante una tarjeta internacional:",
              bold: true,
            },
            {
              type: "ul",
              items: [
                'Pulsa "Pago" en la barra de navegación.',
                'Pulsa "Tarjetas y billeteras".',
                'Selecciona un valor de recarga, elige tu método de pago preferido y pulsa "Confirmar".',
              ],
            },
            { type: "p", text: "Mediante banca en línea:", bold: true },
            {
              type: "ul",
              items: [
                'Pulsa "Pago" en la barra de navegación.',
                'Pulsa "Banca en línea".',
                'Selecciona tu valor de recarga y tu método de pago preferido, luego pulsa "Confirmar".',
                "Serás dirigido a la página de banca en línea de tu banco. Ingresa tu nombre de usuario y contraseña para iniciar sesión.",
                'Revisa los detalles de tu transacción y, si son correctos, pulsa "Continuar con la transacción".',
              ],
            },
          ],
        },
        {
          question:
            "¿Hay comisiones o tipos de cambio asociados con el uso de Grab?",
          content:
            "Las transacciones por encima de cierto monto pueden generar comisiones adicionales. Las comisiones por transacción se comunican mediante notificación en la aplicación. Consulta las políticas de Grab en la aplicación para obtener la información más actualizada. Los tipos de cambio están sujetos a las tarifas del emisor.",
        },
        {
          question:
            "¿Puedo acceder a los servicios de Grab a través de otras aplicaciones que ya conozco?",
          content: [
            "Para los viajeros que prefieren no descargar otra aplicación, Grab también se ha asociado con algunas de las aplicaciones más populares del mundo para ofrecer opciones de acceso a los servicios de transporte por aplicación de Grab a quienes visitan el sudeste asiático.",
            "Usuarios de aplicaciones como WeChat, Alipay, Kakao T y Booking.com pueden acceder a los servicios de transporte por aplicación de Grab en el sudeste asiático a través de estas aplicaciones. Los usuarios de Uber que abran la aplicación en el sudeste asiático también verán un mensaje que los dirige a Grab como la forma recomendada por Uber para viajar en la región.",
            {
              type: "links",
              intro:
                "Para obtener la información y políticas más actualizadas, consulta el sitio web de Grab:",
              items: [
                { label: "Grab – Singapur", href: "https://www.grab.com/sg/" },
                { label: "Grab – Malasia", href: "https://www.grab.com/my/" },
                {
                  label: "Grab – Filipinas",
                  href: "https://www.grab.com/ph/",
                },
                {
                  label: "Grab – Tailandia",
                  href: "https://www.grab.com/th/en/",
                },
              ],
            },
          ],
        },
      ],
    },
  },
};

export default faqs;
